import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FaDownload, FaExclamationTriangle, FaExternalLinkAlt, FaFileInvoiceDollar, FaInfoCircle, FaSave, FaSync } from 'react-icons/fa';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import { PERMISSIONS } from '../constants/permissions';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { apiFetchGLAccounts, apiFetchBillingGroups, apiFetchBillingSheets, resolveBillingSheet } from '../utils/billingSetupApi';
import { quoteLineBilledAmount } from '../utils/billingQuoteAmounts';
import BillingBreadcrumbs from '../components/BillingBreadcrumbs';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const GROUP_ACCENTS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626'];
const GROUP_ACCENT_BG = ['#eff6ff', '#f5f3ff', '#f0fdf4', '#fffbeb', '#fef2f2'];

// ─── helpers ────────────────────────────────────────────────────────────────

function fmt(n) {
    const num = parseFloat(n) || 0;
    return num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function groupGLSubtotal(resident, glAccountsInGroup) {
    return glAccountsInGroup.reduce((s, a) => s + (parseFloat(resident.glValues[a.code]) || 0), 0);
}

function groupUnallocated(resident, groupId) {
    return parseFloat((resident.unallocatedByGroup || {})[groupId]) || 0;
}

function groupSubtotal(resident, glAccountsInGroup, groupId) {
    return groupGLSubtotal(resident, glAccountsInGroup) + groupUnallocated(resident, groupId);
}

function residentTotalBeforeDiscount(resident, glAccountsToSum, displayedGroupIds) {
    const glSum = glAccountsToSum.reduce((s, a) => s + (parseFloat(resident.glValues[a.code]) || 0), 0);
    const unallocSum = Object.entries(resident.unallocatedByGroup || {}).reduce((s, [gId, v]) => {
        if (displayedGroupIds === null || gId === '__none__' || displayedGroupIds.has(gId)) {
            return s + (parseFloat(v) || 0);
        }
        return s;
    }, 0);
    return glSum + unallocSum;
}

function residentNet(resident, glAccountsToSum, displayedGroupIds) {
    return residentTotalBeforeDiscount(resident, glAccountsToSum, displayedGroupIds) * (1 - (parseFloat(resident.discount) || 0) / 100);
}

// ─── storage ────────────────────────────────────────────────────────────────

function buildStorageKey(tenantId, sheetId, siteId, year, month) {
    const sheetPart = sheetId || 'none';
    return `billing_v6_${tenantId}_${sheetPart}_${siteId}_${year}_${month}`;
}

function saveHistoryEntry(tenantId, entry) {
    try {
        const key = `billing_history_${tenantId}`;
        const history = JSON.parse(localStorage.getItem(key) || '[]');
        const idx = history.findIndex(h =>
            h.siteId === entry.siteId
            && h.year === entry.year
            && h.month === entry.month
            && String(h.sheetId || '') === String(entry.sheetId || '')
        );
        if (idx >= 0) history[idx] = entry;
        else history.unshift(entry);
        localStorage.setItem(key, JSON.stringify(history.slice(0, 50)));
    } catch (_) {}
}

// ─── component ──────────────────────────────────────────────────────────────

function BillingPeriod() {
    const navigate = useNavigate();
    const { siteId, year, month } = useParams();
    const [searchParams] = useSearchParams();
    const sheetParam = searchParams.get('sheet') || '';
    const { currentTenant, sites, fetchSites } = useTenant();
    const { hasPermission } = usePermissions();
    const canCreateWorksheet = hasPermission(PERMISSIONS.BILLING_WORKSHEET_CREATE);
    const canUpdateWorksheet = hasPermission(PERMISSIONS.BILLING_WORKSHEET_UPDATE);
    const canUpdateSheet = hasPermission(PERMISSIONS.BILLING_SHEET_UPDATE);

    // residents: [{ customerId, room, name, discount, notes, glValues: { [code]: number }, unallocatedByGroup: { [groupId]: number } }]
    const [residents, setResidents] = useState([]);
    const [prevResidents, setPrevResidents] = useState([]);
    const [billingGroups, setBillingGroups] = useState([]);
    const [glAccounts, setGLAccounts] = useState([]);
    const [billingSheets, setBillingSheets] = useState([]);
    const [activeSheet, setActiveSheet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMsg, setLoadingMsg] = useState('');
    const [loadingSetup, setLoadingSetup] = useState(true);
    const [hasDraft, setHasDraft] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [selectedGroupIds, setSelectedGroupIds] = useState(null); // from billing sheet config
    // Sheet-level under-allocated price items discovered during generate
    // [{ key, name, amount, reason: 'none'|'partial' }]
    const [unallocatedSources, setUnallocatedSources] = useState([]);
    const [showUnallocModal, setShowUnallocModal] = useState(false);
    const [isDraftStatus, setIsDraftStatus] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
    const showToast = (message, variant = 'success') => setToast({ show: true, message, variant });

    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);

    const numMonth = Number(month);
    const monthLabel = `${MONTHS[numMonth - 1]} ${year}`;

    const prevMonth = numMonth === 1 ? 12 : numMonth - 1;
    const prevYear = numMonth === 1 ? Number(year) - 1 : Number(year);
    const prevMonthStr = String(prevMonth).padStart(2, '0');

    const resolvedSheetId = useMemo(() => {
        const sheet = resolveBillingSheet(billingSheets, sheetParam || 'default');
        return sheet?.id || null;
    }, [sheetParam, billingSheets]);

    const storageKey = useMemo(
        () => currentTenant?.id && resolvedSheetId
            ? buildStorageKey(currentTenant.id, resolvedSheetId, siteId, year, month)
            : null,
        [currentTenant?.id, resolvedSheetId, siteId, year, month]
    );

    const prevStorageKey = useMemo(
        () => currentTenant?.id && resolvedSheetId
            ? buildStorageKey(currentTenant.id, resolvedSheetId, siteId, String(prevYear), prevMonthStr)
            : null,
        [currentTenant?.id, resolvedSheetId, siteId, prevYear, prevMonthStr]
    );

    const siteName = useMemo(() => {
        const found = sites?.find(s => String(s.siteId) === String(siteId) || String(s.site_id) === String(siteId));
        return found?.siteName || found?.site_name || `Site ${siteId}`;
    }, [sites, siteId]);

    useEffect(() => {
        if (!currentTenant?.id) return;
        setLoadingSetup(true);
        Promise.all([
            apiFetchBillingGroups(currentTenant.id).then(setBillingGroups).catch(() => setBillingGroups([])),
            apiFetchGLAccounts(currentTenant.id).then(setGLAccounts).catch(() => setGLAccounts([])),
            apiFetchBillingSheets(currentTenant.id).then(setBillingSheets).catch(() => setBillingSheets([])),
        ]).finally(() => setLoadingSetup(false));
    }, [currentTenant?.id]);

    // Apply billing sheet config (groups + care types) when sheet resolves
    useEffect(() => {
        const sheet = resolveBillingSheet(billingSheets, sheetParam || 'default');
        setActiveSheet(sheet);
        if (sheet?.billing_group_ids?.length) {
            setSelectedGroupIds(new Set(sheet.billing_group_ids));
        } else if (sheet) {
            setSelectedGroupIds(new Set());
        } else {
            setSelectedGroupIds(null);
        }
    }, [billingSheets, sheetParam]);

    useEffect(() => {
        if (currentTenant?.id && (!sites || sites.length === 0) && fetchSites) {
            fetchSites(currentTenant.id);
        }
    }, [currentTenant?.id, sites, fetchSites]);

    // Restore tenant-scoped draft on mount
    useEffect(() => {
        if (!storageKey) {
            setResidents([]);
            setUnallocatedSources([]);
            setHasDraft(false);
            setSavedAt(null);
            setIsDraftStatus(true);
            return;
        }
        const raw = localStorage.getItem(storageKey);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                setResidents(parsed.residents || []);
                setUnallocatedSources(parsed.unallocatedSources || []);
                setSavedAt(parsed.savedAt || null);
                setIsDraftStatus(parsed.isDraft !== false);
                setHasDraft(true);
            } catch (_) {}
        } else {
            setResidents([]);
            setUnallocatedSources([]);
            setHasDraft(false);
            setSavedAt(null);
            setIsDraftStatus(true);
        }
    }, [storageKey]);

    // Load previous month's draft for variance comparison (read-only)
    useEffect(() => {
        if (!prevStorageKey) return;
        const raw = localStorage.getItem(prevStorageKey);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                setPrevResidents(parsed.residents || []);
            } catch (_) {}
        } else {
            setPrevResidents([]);
        }
    }, [prevStorageKey]);

    const glById = useMemo(() => {
        const m = {};
        glAccounts.forEach(a => { m[a.id] = a; });
        return m;
    }, [glAccounts]);

    const groupedGL = useMemo(() => {
        const result = {};
        billingGroups.forEach(bg => { result[bg.id] = []; });
        result['__unassigned__'] = [];
        if (!activeSheet) return result;
        // Sheet-scoped membership only — same GL may sit under different groups on
        // different sheets (at most one group per sheet).
        (activeSheet.gl_assignments || [])
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .forEach(as => {
                const acc = glById[as.gl_account_id];
                if (!acc) return;
                if (!result[as.billing_group_id]) result[as.billing_group_id] = [];
                result[as.billing_group_id].push(acc);
            });
        return result;
    }, [billingGroups, activeSheet, glById]);

    const allGLAccounts = useMemo(() =>
        billingGroups.flatMap(bg => groupedGL[bg.id] || []),
        [billingGroups, groupedGL]
    );

    const displayedGroups = useMemo(() => {
        if (selectedGroupIds === null) return [];
        return billingGroups.filter(bg => selectedGroupIds.has(bg.id));
    }, [billingGroups, selectedGroupIds]);

    const displayedGLAccounts = useMemo(() =>
        displayedGroups.flatMap(bg => groupedGL[bg.id] || []),
        [displayedGroups, groupedGL]
    );

    const careTypeFilterIds = useMemo(() => {
        if (activeSheet?.customer_care_type_ids?.length) {
            return activeSheet.customer_care_type_ids.map(String);
        }
        return null;
    }, [activeSheet]);

    // Map GL account code → its billing group ID on the active sheet.
    const codeToGroupId = useMemo(() => {
        const map = {};
        if (!activeSheet) return map;
        (activeSheet.gl_assignments || []).forEach(as => {
            const acc = glById[as.gl_account_id];
            if (acc?.code) map[acc.code] = as.billing_group_id;
        });
        glAccounts.forEach(a => { if (a.code && !(a.code in map)) map[a.code] = '__none__'; });
        return map;
    }, [glAccounts, activeSheet, glById]);

    // ── data loading ──────────────────────────────────────────────────────

    const loadFromAPI = useCallback(async () => {
        if (!currentTenant?.id || !canCreateWorksheet) return;
        if (!activeSheet) {
            alert('Configure a billing sheet first (groups, care types, and GL placements), then reload.');
            return;
        }
        setLoading(true);
        setLoadingMsg('Loading price item GL allocations…');

        try {
            // Build GL allocations lookup from price items across all sites
            const glAllocLookup = {};
            try {
                const sitesRes = await fetchWithAuth(`${API_BASE_URL}/sites?tenant_id=${currentTenant.id}`);
                if (sitesRes.ok) {
                    const sitesData = await sitesRes.json();
                    const tenantSites = Array.isArray(sitesData) ? sitesData : [];
                    await Promise.all(tenantSites.map(async (ts) => {
                        const tsId = ts.id || ts.site_id;
                        try {
                            const res = await fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-price-list-items?tenant_id=${currentTenant.id}&site_id=${tsId}`
                            );
                            if (res.ok) {
                                const items = await res.json();
                                (Array.isArray(items) ? items : []).forEach(item => {
                                    const key = item.price_list_item_key;
                                    if (!key || glAllocLookup[key]) return;
                                    const meta = item.metadata || {};
                                    const price = parseFloat(meta.price || item.price) || 0;
                                    let allocations = meta.gl_allocations;
                                    if (!allocations && meta.gl_group) {
                                        allocations = [{ gl_account_code: meta.gl_group, amount: price }];
                                    }
                                    glAllocLookup[key] = {
                                        gl_allocations: allocations || [],
                                        price,
                                        name: meta.item_name || item.item_name || key,
                                    };
                                });
                            }
                        } catch (_) {}
                    }));
                }
            } catch (_) {}

            setLoadingMsg('Loading care recipients…');
            const recipientsRes = await fetchWithAuth(`${API_BASE_URL}/care-recipients?include_sites=true`);
            if (!recipientsRes.ok) throw new Error('Failed to fetch care recipients');
            const allRecipients = await recipientsRes.json();

            const siteRecipients = allRecipients.filter(r => {
                const sitesArr = r.sites || [];
                return (
                    String(r.organisational_site_id) === String(siteId) ||
                    String(r.site_id) === String(siteId) ||
                    sitesArr.some(s => String(s.site_id) === String(siteId))
                );
            });

            // Only include residents who were active during the billing period.
            // We filter by status field — ACTIVE residents are included; INACTIVE/ARCHIVED are excluded.
            const billingPeriodStart = new Date(Number(year), Number(month) - 1, 1);
            const billingPeriodEndDate = new Date(Number(year), Number(month), 0, 23, 59, 59);
            const activeAtPeriod = siteRecipients.filter(r => {
                const status = (r.status || r.Status || '').toUpperCase();
                if (status && status !== 'ACTIVE') return false;
                if (careTypeFilterIds?.length) {
                    const ctId = String(r.customer_care_type_id || '');
                    if (!careTypeFilterIds.includes(ctId)) return false;
                }
                // If the API returns discharge/admission dates in the future, filter by them:
                const dischargeDate = r.discharge_date || r.date_discharged || r.date_of_discharge;
                if (dischargeDate) {
                    const d = new Date(dischargeDate);
                    if (!isNaN(d.getTime()) && d < billingPeriodStart) return false;
                }
                const admissionDate = r.admission_date || r.date_admitted || r.date_of_admission;
                if (admissionDate) {
                    const d = new Date(admissionDate);
                    if (!isNaN(d.getTime()) && d > billingPeriodEndDate) return false;
                }
                return true;
            });

            if (activeAtPeriod.length === 0) {
                setResidents([]);
                setUnallocatedSources([]);
                setLoading(false);
                setLoadingMsg('');
                return;
            }

            setLoadingMsg(`Loading billing data for ${activeAtPeriod.length} residents…`);

            // Aggregate under-allocated price items across all residents on this sheet
            const sourcesByKey = {};
            const recordUnallocatedSource = (key, name, amount, reason) => {
                if (!key || !(amount > 0)) return;
                const existing = sourcesByKey[key];
                if (!existing) {
                    sourcesByKey[key] = {
                        key,
                        name: name || key,
                        amount,
                        reason,
                    };
                } else {
                    existing.amount += amount;
                    if (existing.reason !== reason) existing.reason = 'mixed';
                    if (!existing.name || existing.name === key) existing.name = name || existing.name;
                }
            };

            const residentData = await Promise.all(
                activeAtPeriod.map(async (r) => {
                    const glValues = {};
                    const unallocatedByGroup = {};

                    try {
                        // Fetch care plans and quotations in parallel
                        const [carePlansRes, quotListRes] = await Promise.all([
                            fetchWithAuth(`${API_BASE_URL}/careplans/${r.customer_id}`),
                            fetchWithAuth(`${API_BASE_URL}/quotation/${r.customer_id}`),
                        ]);

                        if (quotListRes.ok) {
                            const quotListData = await quotListRes.json();
                            const quotationsArr = quotListData.Quotations || quotListData.quotations || [];

                            // Build set of care plan IDs belonging to this site, matched by site_name.
                            // null = fetch failed (can't determine); Set = authoritative (may be empty).
                            let siteCarePlanIds = null;
                            if (carePlansRes.ok) {
                                const cpData = await carePlansRes.json();
                                const plans = cpData.care_plans || cpData.carePlans || (Array.isArray(cpData) ? cpData : []);
                                siteCarePlanIds = new Set();
                                plans.forEach(p => {
                                    const planId = p.care_plan_id || p.CarePlanID;
                                    const planSiteName = p.site_name || p.SiteName;
                                    if (planId && planSiteName && siteName &&
                                        planSiteName.toLowerCase() === siteName.toLowerCase()) {
                                        siteCarePlanIds.add(String(planId));
                                    }
                                });
                            }

                            // Filter quotations to this site.
                            // Care plan set is authoritative: empty set → no quotations for this site.
                            // Only fall back if the care plan fetch itself failed (siteCarePlanIds is null).
                            const siteQuotations = siteCarePlanIds !== null
                                ? quotationsArr.filter(q => {
                                    const cpId = q.care_plan_id || q.CarePlanID;
                                    return cpId != null && siteCarePlanIds.has(String(cpId));
                                })
                                : quotationsArr.filter(q =>
                                    !q.site_name || (siteName && q.site_name.toLowerCase() === siteName.toLowerCase())
                                );

                            // Last moment of the billing period month — quotations created after this are excluded
                            const billingPeriodEnd = new Date(Number(year), Number(month), 0, 23, 59, 59);

                            // Active quotations for this site that existed on or before the end of the billing period
                            const activeEligible = siteQuotations.filter(q => {
                                if (q.status !== 1) return false;
                                if (q.date_created && new Date(q.date_created) > billingPeriodEnd) return false;
                                return true;
                            });

                            const pool = activeEligible.length > 0
                                ? activeEligible
                                : siteQuotations.filter(q =>
                                    q.status !== 2 && q.status !== 3 &&
                                    (!q.date_created || new Date(q.date_created) <= billingPeriodEnd)
                                );

                            // Pick the most recently created; highest quotation_id as tiebreaker
                            const activeQuotation = pool.reduce((best, q) => {
                                if (!best) return q;
                                const bestTime = best.date_created ? new Date(best.date_created).getTime() : 0;
                                const qTime = q.date_created ? new Date(q.date_created).getTime() : 0;
                                if (qTime !== bestTime) return qTime > bestTime ? q : best;
                                return q.quotation_id > best.quotation_id ? q : best;
                            }, null);

                            if (activeQuotation) {
                                const quotId = activeQuotation.quotation_id || activeQuotation.QuotationID;
                                const detailRes = await fetchWithAuth(`${API_BASE_URL}/quotations/${quotId}`);
                                if (detailRes.ok) {
                                    const detail = await detailRes.json();
                                    const lineItems = detail.line_items || detail.lineItems || [];

                                    lineItems.forEach(item => {
                                        const itemKey = item.price_list_item_key || item.item_key || '';
                                        const itemName = item.item_name || item.description
                                            || glAllocLookup[itemKey]?.name || itemKey;
                                        const lineTotal = quoteLineBilledAmount(item);

                                        const detached = Boolean(item.catalogue_detached || item.detached_at);
                                        const snapshotAllocs = Array.isArray(item.gl_allocations)
                                            ? item.gl_allocations
                                            : (Array.isArray(item.catalogue_snapshot?.gl_allocations)
                                                ? item.catalogue_snapshot.gl_allocations
                                                : []);
                                        const liveAllocInfo = detached ? null : glAllocLookup[itemKey];
                                        const allocInfo = snapshotAllocs.length > 0
                                            ? {
                                                gl_allocations: snapshotAllocs,
                                                price: parseFloat(item.unit_price || item.price) || liveAllocInfo?.price || 0,
                                                name: itemName,
                                            }
                                            : liveAllocInfo;
                                        if (allocInfo && allocInfo.gl_allocations.length > 0) {
                                            let allocated = 0;
                                            const allocPerGroup = {};
                                            allocInfo.gl_allocations.forEach(alloc => {
                                                if (alloc.gl_account_code) {
                                                    const ratio = allocInfo.price > 0 ? lineTotal / allocInfo.price : 1;
                                                    const scaledAmount = (parseFloat(alloc.amount) || 0) * ratio;
                                                    glValues[alloc.gl_account_code] = (glValues[alloc.gl_account_code] || 0) + scaledAmount;
                                                    allocated += scaledAmount;
                                                    const gId = codeToGroupId[alloc.gl_account_code] || '__none__';
                                                    allocPerGroup[gId] = (allocPerGroup[gId] || 0) + scaledAmount;
                                                }
                                            });
                                            const remaining = Math.max(0, lineTotal - allocated);
                                            if (remaining > 0) {
                                                recordUnallocatedSource(itemKey, itemName, remaining, 'partial');
                                                const totalAlloc = Object.values(allocPerGroup).reduce((s, v) => s + v, 0);
                                                if (totalAlloc > 0) {
                                                    Object.entries(allocPerGroup).forEach(([gId, gAmt]) => {
                                                        unallocatedByGroup[gId] = (unallocatedByGroup[gId] || 0) + remaining * (gAmt / totalAlloc);
                                                    });
                                                } else {
                                                    unallocatedByGroup['__none__'] = (unallocatedByGroup['__none__'] || 0) + remaining;
                                                }
                                            }
                                        } else {
                                            // No GL allocations — entire amount is unallocated (general)
                                            recordUnallocatedSource(itemKey || `unnamed:${itemName}`, itemName || 'Unknown item', lineTotal, 'none');
                                            unallocatedByGroup['__none__'] = (unallocatedByGroup['__none__'] || 0) + lineTotal;
                                        }
                                    });
                                }
                            }
                        }
                    } catch (_) {}

                    // Round all computed amounts to 2dp to avoid floating-point display noise
                    Object.keys(glValues).forEach(k => {
                        glValues[k] = Math.round(glValues[k] * 100) / 100;
                    });
                    Object.keys(unallocatedByGroup).forEach(k => {
                        unallocatedByGroup[k] = Math.round(unallocatedByGroup[k] * 100) / 100;
                    });

                    const surname = r.surname || '';
                    const name = r.name || '';
                    const fullName = surname && name ? `${surname}, ${name}` : surname || name;

                    return {
                        customerId: r.customer_id,
                        room: r.room_number || '',
                        name: fullName,
                        discount: parseFloat(r.discount || 0),
                        notes: '',
                        glValues,
                        unallocatedByGroup,
                    };
                })
            );

            residentData.sort((a, b) => {
                const ra = parseInt(a.room, 10), rb = parseInt(b.room, 10);
                if (!isNaN(ra) && !isNaN(rb)) return ra - rb;
                return String(a.room).localeCompare(String(b.room));
            });

            setResidents(residentData);
            const sourcesList = Object.values(sourcesByKey)
                .map(s => ({ ...s, amount: Math.round(s.amount * 100) / 100 }))
                .sort((a, b) => b.amount - a.amount);
            setUnallocatedSources(sourcesList);
            setHasDraft(true);
            setIsDraftStatus(true);
            const now = new Date().toISOString();
            if (storageKey) {
                localStorage.setItem(storageKey, JSON.stringify({
                    residents: residentData,
                    unallocatedSources: sourcesList,
                    savedAt: now,
                    isDraft: true,
                }));
            }
            setSavedAt(now);
            saveHistoryEntry(currentTenant.id, {
                siteId, siteName, year, month,
                sheetId: resolvedSheetId || null,
                sheetName: activeSheet?.name || 'Billing sheet',
                isDraft: true, lastAccessed: now,
            });

        } catch (err) {
            console.error('Error loading billing data:', err);
            alert('Failed to load billing data: ' + err.message);
        } finally {
            setLoading(false);
            setLoadingMsg('');
        }
    }, [currentTenant?.id, siteId, year, month, siteName, storageKey, codeToGroupId, careTypeFilterIds, resolvedSheetId, activeSheet, canCreateWorksheet]);

    // ── editing ───────────────────────────────────────────────────────────

    const updateGLValue = (rIdx, glCode, value) => {
        if (!canUpdateWorksheet) return;
        setResidents(prev => {
            const next = [...prev];
            next[rIdx] = { ...next[rIdx], glValues: { ...next[rIdx].glValues, [glCode]: parseFloat(value) || 0 } };
            return next;
        });
    };

    const updateUnallocatedForGroup = (rIdx, groupId, value) => {
        if (!canUpdateWorksheet) return;
        setResidents(prev => {
            const next = [...prev];
            next[rIdx] = {
                ...next[rIdx],
                unallocatedByGroup: { ...(next[rIdx].unallocatedByGroup || {}), [groupId]: parseFloat(value) || 0 },
            };
            return next;
        });
    };

    const updateResident = (rIdx, field, value) => {
        if (!canUpdateWorksheet) return;
        setResidents(prev => {
            const next = [...prev];
            next[rIdx] = { ...next[rIdx], [field]: value };
            return next;
        });
    };

    // ── persistence ───────────────────────────────────────────────────────

    const persistWorksheet = (asDraft) => {
        if (!storageKey || !currentTenant?.id || !canUpdateWorksheet) return;
        const now = new Date().toISOString();
        localStorage.setItem(storageKey, JSON.stringify({
            residents,
            unallocatedSources,
            savedAt: now,
            isDraft: asDraft,
            selectedGroupIds: selectedGroupIds ? [...selectedGroupIds] : null,
        }));
        setSavedAt(now);
        setIsDraftStatus(asDraft);
        setHasDraft(true);
        saveHistoryEntry(currentTenant.id, {
            siteId, siteName, year, month,
            sheetId: resolvedSheetId || null,
            sheetName: activeSheet?.name || 'Billing sheet',
            isDraft: asDraft,
            lastAccessed: now,
        });
        showToast(asDraft ? 'Draft saved' : 'Worksheet saved — find it under Worksheets');
    };

    const saveDraft = () => persistWorksheet(true);
    const saveWorksheet = () => persistWorksheet(false);

    // ── CSV export ────────────────────────────────────────────────────────

    const exportCSV = () => {
        const lines = [];

        // Header row 1: billing group name spans + trailing columns
        const headerRow1 = [siteName, '', ...displayedGroups.flatMap(bg => {
            const bgAccounts = groupedGL[bg.id] || [];
            const span = bgAccounts.length + 2; // N GL cols + Unalloc + Subtotal
            return [bg.name, ...Array(span - 1).fill('')];
        }), 'Unallocated', 'Total Before Disc', 'Disc %', 'Net Total', 'Notes'];
        lines.push(headerRow1.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

        // Header row 2: GL account codes
        const headerRow2 = ['Room', 'Resident', ...displayedGroups.flatMap(bg => {
            const bgAccounts = groupedGL[bg.id] || [];
            return [...bgAccounts.map(a => a.code), 'Unalloc', `${bg.name} Total`];
        }), '', '', '', '', ''];
        lines.push(headerRow2.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

        // Header row 3: GL account names
        const headerRow3 = ['', '', ...displayedGroups.flatMap(bg => {
            const bgAccounts = groupedGL[bg.id] || [];
            return [...bgAccounts.map(a => a.name), '', ''];
        }), '', '', '', '', ''];
        lines.push(headerRow3.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

        residents.forEach(resident => {
            const totalBefore = residentTotalBeforeDiscount(resident, displayedGLAccounts, selectedGroupIds);
            const net = residentNet(resident, displayedGLAccounts, selectedGroupIds);
            const row = [
                `"${resident.room}"`,
                `"${resident.name}"`,
                ...displayedGroups.flatMap(bg => {
                    const bgAccounts = groupedGL[bg.id] || [];
                    const unalloc = groupUnallocated(resident, bg.id);
                    const sub = groupSubtotal(resident, bgAccounts, bg.id);
                    return [
                        ...bgAccounts.map(a => (parseFloat(resident.glValues[a.code]) || 0).toFixed(2)),
                        unalloc.toFixed(2),
                        sub.toFixed(2),
                    ];
                }),
                groupUnallocated(resident, '__none__').toFixed(2),
                totalBefore.toFixed(2),
                (parseFloat(resident.discount) || 0).toFixed(2),
                net.toFixed(2),
                `"${(resident.notes || '').replace(/"/g, '""')}"`,
            ];
            lines.push(row.join(','));
        });

        // Grand total row
        const grandRow = ['"TOTAL"', '',
            ...displayedGroups.flatMap(bg => {
                const bgAccounts = groupedGL[bg.id] || [];
                return [
                    ...bgAccounts.map(a => residents.reduce((s, r) => s + (parseFloat(r.glValues[a.code]) || 0), 0).toFixed(2)),
                    residents.reduce((s, r) => s + groupUnallocated(r, bg.id), 0).toFixed(2),
                    residents.reduce((s, r) => s + groupSubtotal(r, bgAccounts, bg.id), 0).toFixed(2),
                ];
            }),
            residents.reduce((s, r) => s + groupUnallocated(r, '__none__'), 0).toFixed(2),
            residents.reduce((s, r) => s + residentTotalBeforeDiscount(r, displayedGLAccounts, selectedGroupIds), 0).toFixed(2),
            '',
            residents.reduce((s, r) => s + residentNet(r, displayedGLAccounts, selectedGroupIds), 0).toFixed(2),
            '',
        ];
        lines.push(grandRow.join(','));

        const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `billing_${siteName.replace(/\s+/g, '_')}_${year}_${month}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // ── totals ────────────────────────────────────────────────────────────

    const grandTotalBefore = useMemo(() =>
        residents.reduce((s, r) => s + residentTotalBeforeDiscount(r, displayedGLAccounts, selectedGroupIds), 0),
        [residents, displayedGLAccounts, selectedGroupIds]
    );
    const grandNet = useMemo(() =>
        residents.reduce((s, r) => s + residentNet(r, displayedGLAccounts, selectedGroupIds), 0),
        [residents, displayedGLAccounts, selectedGroupIds]
    );
    const grandNoneUnalloc = useMemo(() =>
        residents.reduce((s, r) => s + groupUnallocated(r, '__none__'), 0),
        [residents]
    );

    const hasPrevData = prevResidents.length > 0;

    const grandVariance = useMemo(() => {
        if (!hasPrevData) return null;
        const cur = residents.reduce((s, r) => s + residentNet(r, displayedGLAccounts, selectedGroupIds), 0);
        const prev = prevResidents.reduce((s, r) => s + residentNet(r, displayedGLAccounts, selectedGroupIds), 0);
        return cur - prev;
    }, [residents, prevResidents, displayedGLAccounts, selectedGroupIds, hasPrevData]);

    const columnTotals = useMemo(() => {
        const totals = {};
        allGLAccounts.forEach(a => {
            totals[a.code] = residents.reduce((s, r) => s + (parseFloat(r.glValues[a.code]) || 0), 0);
        });
        return totals;
    }, [residents, allGLAccounts]);

    const groupUnallocTotals = useMemo(() => {
        const totals = {};
        displayedGroups.forEach(bg => {
            totals[bg.id] = residents.reduce((s, r) => s + groupUnallocated(r, bg.id), 0);
        });
        return totals;
    }, [residents, displayedGroups]);

    const savedAtLabel = savedAt
        ? new Date(savedAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })
        : null;

    const hasData = residents.length > 0;
    const hasGLSetup = billingGroups.length > 0 && glAccounts.length > 0;

    // Each group spans: N GL cols + 1 Unalloc col + 1 Subtotal col
    const groupColSpan = (bgId) => (groupedGL[bgId]?.length || 0) + 2;

    const unallocTipText = 'Click to open unallocated price items and assign GL allocations';

    const UnallocInfoIcon = ({ style, onClick }) => (
        <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 80 }}
            overlay={<Tooltip>{unallocTipText}</Tooltip>}
        >
            <span
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); } }}
                style={{ display: 'inline-flex', cursor: 'pointer', ...style }}
            >
                <FaInfoCircle />
            </span>
        </OverlayTrigger>
    );

    // ── render ────────────────────────────────────────────────────────────

    return (
        <div style={{ padding: '20px', backgroundColor: 'var(--brand-cream, #fafaf7)', minHeight: '100%' }}>

            <BillingBreadcrumbs items={[
                { label: 'Billing', to: '/billing' },
                { label: 'Worksheets', to: '/billing?tab=worksheets' },
                { label: `${siteName} · ${monthLabel}` },
            ]} />

            {/* Page header */}
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h2 style={{ margin: 0, lineHeight: 1.2 }}>{siteName}</h2>
                    <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.95rem', marginTop: '2px' }}>{monthLabel}</div>
                    {activeSheet ? (
                        <div style={{ color: 'var(--theme-text-body)', fontSize: '0.85rem', marginTop: '4px', fontWeight: 500 }}>
                            Sheet: {activeSheet.name}
                            {activeSheet.is_default && <span style={{ color: 'var(--theme-text-muted)', fontWeight: 400 }}> (default)</span>}
                            {canUpdateSheet && (
                                <>
                                    {' · '}
                                    <Link to="/billing-sheets" style={{ color: 'var(--theme-text-secondary)', fontWeight: 500, fontSize: '0.82rem' }}>
                                        Edit sheet config
                                    </Link>
                                </>
                            )}
                        </div>
                    ) : !loadingSetup ? (
                        <div style={{ color: '#b45309', fontSize: '0.85rem', marginTop: '4px' }}>
                            No billing sheet selected — configure one under Billing Sheets.
                        </div>
                    ) : null}
                    {savedAtLabel && (
                        <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.8rem', marginTop: '2px' }}>
                            {isDraftStatus ? 'Draft' : 'Saved'} · {savedAtLabel}
                        </div>
                    )}
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate('/billing?tab=worksheets')}
                    >
                        Back to worksheets
                    </Button>
                    {canUpdateWorksheet && (
                        <Button variant="outline-secondary" size="sm" onClick={saveDraft} disabled={!hasData || !activeSheet}>
                            <FaSave style={{ marginRight: '5px' }} />Save Draft
                        </Button>
                    )}
                    {canUpdateWorksheet && (
                        <Button variant="dark" size="sm" onClick={saveWorksheet} disabled={!hasData || !activeSheet}>
                            <FaSave style={{ marginRight: '5px' }} />Save
                        </Button>
                    )}
                    <Button variant="outline-success" size="sm" onClick={exportCSV} disabled={!hasData || !hasGLSetup || !activeSheet}>
                        <FaDownload style={{ marginRight: '5px' }} />Export CSV
                    </Button>
                    {canCreateWorksheet && (
                        <Button variant="primary" size="sm" onClick={loadFromAPI} disabled={loading || !activeSheet}>
                            {loading
                                ? <Spinner size="sm" animation="border" style={{ marginRight: '6px' }} />
                                : <FaSync style={{ marginRight: '5px' }} />
                            }
                            {hasDraft ? 'Reload from System' : 'Load from System'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Loading message (API reload) */}
            {loading && loadingMsg && (
                <div className="d-flex align-items-center gap-2 mb-3" style={{ color: 'var(--theme-text-secondary)', fontSize: '0.9rem' }}>
                    <Spinner size="sm" animation="border" /><span>{loadingMsg}</span>
                </div>
            )}

            {/* Setup loading spinner */}
            {loadingSetup && (
                <div className="d-flex align-items-center justify-content-center gap-2" style={{ padding: '80px 0', color: 'var(--theme-text-secondary)', fontSize: '0.9rem' }}>
                    <Spinner size="sm" animation="border" /><span>Loading billing setup…</span>
                </div>
            )}

            {/* No billing sheet configured */}
            {!loadingSetup && !activeSheet && (
                <div style={{
                    backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px',
                    padding: '16px 20px', marginBottom: '20px', fontSize: '0.9rem', color: '#92400e',
                }}>
                    <strong>No billing sheet available.</strong> Create a billing sheet and assign GL accounts
                    to groups on that sheet under{' '}
                    <Link to="/billing-sheets" style={{ color: '#92400e', fontWeight: 600 }}>Billing Sheets</Link>.
                </div>
            )}

            {/* No GL setup warning */}
            {!loadingSetup && activeSheet && !hasGLSetup && (
                <div style={{
                    backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px',
                    padding: '16px 20px', marginBottom: '20px', fontSize: '0.9rem', color: '#92400e',
                }}>
                    <strong>No GL accounts defined.</strong> Go to{' '}
                    <Link to="/gl-accounts" style={{ color: '#92400e', fontWeight: 600 }}>GL Accounts</Link>
                    {' '}to create accounts, then assign them to groups on this billing sheet.
                </div>
            )}

            {/* Empty state */}
            {!loadingSetup && activeSheet && !loading && !hasData && (
                <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--theme-text-muted)' }}>
                    <FaFileInvoiceDollar style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.4 }} />
                    <p style={{ fontSize: '1rem', marginBottom: '16px' }}>No billing data loaded yet.</p>
                    {canCreateWorksheet ? (
                        <Button variant="primary" onClick={loadFromAPI} disabled={loading}>Load from System</Button>
                    ) : (
                        <p style={{ fontSize: '0.9rem' }}>Ask a billing manager to generate this period.</p>
                    )}
                </div>
            )}

            {/* Billing table */}
            {!loadingSetup && hasData && (
                <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid #dee2e6', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                            {/* Row 1: billing group names */}
                            <tr style={{ backgroundColor: '#fff' }}>
                                <th style={{ ...TH, color: '#6b7280', borderBottom: '2px solid #e5e7eb' }} rowSpan={3}>Room</th>
                                <th style={{ ...TH, minWidth: '160px', textAlign: 'left', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }} rowSpan={3}>Resident</th>
                                {displayedGroups.map((bg, bgIdx) => {
                                    const bgAccounts = groupedGL[bg.id] || [];
                                    if (bgAccounts.length === 0) return null;
                                    const accent = GROUP_ACCENTS[bgIdx % GROUP_ACCENTS.length];
                                    const accentBg = GROUP_ACCENT_BG[bgIdx % GROUP_ACCENT_BG.length];
                                    return (
                                        <th key={bg.id} colSpan={groupColSpan(bg.id)} style={{
                                            ...TH,
                                            textAlign: 'center',
                                            color: accent,
                                            backgroundColor: accentBg,
                                            borderLeft: `3px solid ${accent}`,
                                            borderBottom: `2px solid ${accent}`,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.07em',
                                            fontSize: '0.68rem',
                                        }}>
                                            {bg.name}
                                        </th>
                                    );
                                })}
                                <th
                                    style={{
                                        ...TH, textAlign: 'right', minWidth: '110px', borderLeft: '2px solid #e5e7eb',
                                        color: unallocatedSources.length ? '#c2410c' : '#6b7280',
                                        borderBottom: '2px solid #e5e7eb',
                                        cursor: unallocatedSources.length ? 'pointer' : 'default',
                                    }}
                                    rowSpan={3}
                                    onClick={() => unallocatedSources.length && setShowUnallocModal(true)}
                                >
                                    Unallocated
                                    {unallocatedSources.length > 0 && (
                                        <UnallocInfoIcon
                                            style={{ marginLeft: '6px', fontSize: '0.75rem', verticalAlign: 'middle' }}
                                            onClick={(e) => { e.stopPropagation(); setShowUnallocModal(true); }}
                                        />
                                    )}
                                </th>
                                <th style={{ ...TH, textAlign: 'right', minWidth: '115px', borderLeft: '2px solid #e5e7eb', color: '#374151', borderBottom: '2px solid #e5e7eb' }} rowSpan={3}>
                                    Total Before Disc
                                </th>
                                <th style={{ ...TH, textAlign: 'right', width: '70px', borderLeft: '2px solid #e5e7eb', color: '#374151', borderBottom: '2px solid #e5e7eb' }} rowSpan={3}>Disc %</th>
                                <th style={{ ...TH, textAlign: 'right', minWidth: '100px', color: '#16a34a', borderBottom: '2px solid #e5e7eb' }} rowSpan={3}>Net Total</th>
                                <th style={{ ...TH, textAlign: 'right', minWidth: '95px', color: '#7c3aed', borderBottom: '2px solid #e5e7eb', borderLeft: '1px solid #e5e7eb' }} rowSpan={3}>
                                    Δ Prev{hasPrevData ? '' : <span style={{ display: 'block', fontSize: '0.6rem', color: '#9ca3af', fontWeight: 400 }}>no data</span>}
                                </th>
                                <th style={{ ...TH, minWidth: '130px', textAlign: 'left', color: '#6b7280', borderBottom: '2px solid #e5e7eb' }} rowSpan={3}>Notes</th>
                            </tr>
                            {/* Row 2: GL account codes */}
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                {displayedGroups.flatMap((bg, bgIdx) => {
                                    const bgAccounts = groupedGL[bg.id] || [];
                                    if (bgAccounts.length === 0) return [];
                                    const accent = GROUP_ACCENTS[bgIdx % GROUP_ACCENTS.length];
                                    const accentBg = GROUP_ACCENT_BG[bgIdx % GROUP_ACCENT_BG.length];
                                    return [
                                        ...bgAccounts.map((a, aIdx) => (
                                            <th key={a.code} style={{
                                                ...TH, textAlign: 'right', minWidth: '90px',
                                                color: '#374151',
                                                backgroundColor: accentBg,
                                                borderLeft: aIdx === 0 ? `3px solid ${accent}` : undefined,
                                                fontSize: '0.72rem', fontFamily: 'monospace', letterSpacing: '0.05em',
                                            }}>
                                                {a.code}
                                            </th>
                                        )),
                                        <th
                                            key={`${bg.id}-unalloc`}
                                            style={{
                                                ...TH, textAlign: 'right', minWidth: '90px',
                                                backgroundColor: '#eff6ff',
                                                color: unallocatedSources.length ? '#c2410c' : '#3b82f6',
                                                fontSize: '0.72rem',
                                                cursor: unallocatedSources.length ? 'pointer' : 'default',
                                            }}
                                            onClick={() => unallocatedSources.length && setShowUnallocModal(true)}
                                        >
                                            Unalloc
                                            {unallocatedSources.length > 0 && (
                                                <UnallocInfoIcon
                                                    style={{ marginLeft: '4px', fontSize: '0.65rem', verticalAlign: 'middle' }}
                                                    onClick={(e) => { e.stopPropagation(); setShowUnallocModal(true); }}
                                                />
                                            )}
                                        </th>,
                                        <th key={`${bg.id}-sub`} style={{
                                            ...TH, textAlign: 'right', minWidth: '90px',
                                            backgroundColor: '#f3f4f6', color: '#374151', fontSize: '0.72rem',
                                        }}>
                                            Subtotal
                                        </th>,
                                    ];
                                })}
                            </tr>
                            {/* Row 3: GL account names */}
                            <tr>
                                {displayedGroups.flatMap((bg, bgIdx) => {
                                    const bgAccounts = groupedGL[bg.id] || [];
                                    if (bgAccounts.length === 0) return [];
                                    const accent = GROUP_ACCENTS[bgIdx % GROUP_ACCENTS.length];
                                    const accentBg = GROUP_ACCENT_BG[bgIdx % GROUP_ACCENT_BG.length];
                                    return [
                                        ...bgAccounts.map((a, aIdx) => (
                                            <th key={a.code} style={{
                                                ...TH, textAlign: 'right', fontSize: '0.7rem', fontWeight: 400,
                                                color: '#9ca3af',
                                                backgroundColor: accentBg,
                                                borderLeft: aIdx === 0 ? `3px solid ${accent}` : undefined,
                                            }}>
                                                {a.name}
                                            </th>
                                        )),
                                        <th key={`${bg.id}-unalloc-name`} style={{ ...TH, backgroundColor: '#f0f9ff' }} />,
                                        <th key={`${bg.id}-sub-name`} style={{ ...TH, backgroundColor: '#f9fafb' }} />,
                                    ];
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {residents.map((resident, rIdx) => {
                                const totalBefore = residentTotalBeforeDiscount(resident, displayedGLAccounts, selectedGroupIds);
                                const net = residentNet(resident, displayedGLAccounts, selectedGroupIds);
                                return (
                                    <tr key={resident.customerId} style={{
                                        backgroundColor: rIdx % 2 === 0 ? '#fff' : '#f9fafb',
                                        borderTop: '1px solid #e5e7eb',
                                    }}>
                                        <td style={{ ...TD, textAlign: 'center', fontWeight: 700, color: '#374151', width: '50px' }}>
                                            {resident.room}
                                        </td>
                                        <td style={{ ...TD, fontWeight: 600, color: '#111827' }}>
                                            {resident.name}
                                        </td>
                                        {displayedGroups.flatMap(bg => {
                                            const bgAccounts = groupedGL[bg.id] || [];
                                            if (bgAccounts.length === 0) return [];
                                            const unalloc = groupUnallocated(resident, bg.id);
                                            const sub = groupSubtotal(resident, bgAccounts, bg.id);
                                            return [
                                                ...bgAccounts.map((a, aIdx) => (
                                                    <td key={a.code} style={{
                                                        ...TD, padding: '2px 4px',
                                                        borderLeft: aIdx === 0 ? '2px solid #e5e7eb' : undefined,
                                                    }}>
                                                        <input
                                                            type="number"
                                                            value={parseFloat(resident.glValues[a.code]) || ''}
                                                            onChange={e => updateGLValue(rIdx, a.code, e.target.value)}
                                                            readOnly={!canUpdateWorksheet}
                                                            disabled={!canUpdateWorksheet}
                                                            placeholder="0.00"
                                                            style={NUM_INPUT}
                                                            min={0} step="0.01"
                                                        />
                                                    </td>
                                                )),
                                                <td key={`${bg.id}-unalloc`} style={{ ...TD, padding: '2px 4px', backgroundColor: '#eff6ff' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                        <input
                                                            type="number"
                                                            value={unalloc || ''}
                                                            onChange={e => updateUnallocatedForGroup(rIdx, bg.id, e.target.value)}
                                                            placeholder="0.00"
                                                            style={{ ...NUM_INPUT, backgroundColor: '#eff6ff' }}
                                                            min={0} step="0.01"
                                                            disabled={!canUpdateWorksheet}
                                                        />
                                                        {unallocatedSources.length > 0 && unalloc > 0 && (
                                                            <UnallocInfoIcon
                                                                style={{ color: '#c2410c', fontSize: '0.72rem', padding: '2px' }}
                                                                onClick={() => setShowUnallocModal(true)}
                                                            />
                                                        )}
                                                    </div>
                                                </td>,
                                                <td key={`${bg.id}-sub`} style={{
                                                    ...TD, textAlign: 'right', fontWeight: 600,
                                                    color: '#374151', backgroundColor: '#f3f4f6',
                                                    whiteSpace: 'nowrap', fontSize: '0.82rem',
                                                }}>
                                                    {fmt(sub)}
                                                </td>,
                                            ];
                                        })}
                                        {/* General unallocated (items with no GL allocation) */}
                                        <td style={{ ...TD, padding: '2px 4px', borderLeft: '2px solid #e5e7eb', backgroundColor: '#f0f9ff' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                <input
                                                    type="number"
                                                    value={groupUnallocated(resident, '__none__') || ''}
                                                    onChange={e => updateUnallocatedForGroup(rIdx, '__none__', e.target.value)}
                                                    placeholder="0.00"
                                                    style={{ ...NUM_INPUT, backgroundColor: '#f0f9ff' }}
                                                    min={0} step="0.01"
                                                    disabled={!canUpdateWorksheet}
                                                />
                                                {unallocatedSources.length > 0 && groupUnallocated(resident, '__none__') > 0 && (
                                                    <UnallocInfoIcon
                                                        style={{ color: '#c2410c', fontSize: '0.72rem', padding: '2px' }}
                                                        onClick={() => setShowUnallocModal(true)}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                        {/* Total Before Discount */}
                                        <td style={{
                                            ...TD, textAlign: 'right', fontWeight: 600,
                                            color: '#374151', backgroundColor: '#f8fafc',
                                            whiteSpace: 'nowrap', borderLeft: '2px solid #e5e7eb',
                                        }}>
                                            {fmt(totalBefore)}
                                        </td>
                                        {/* Discount % */}
                                        <td style={{ ...TD, padding: '2px 4px', borderLeft: '2px solid #e5e7eb' }}>
                                            <input
                                                type="number"
                                                value={resident.discount}
                                                onChange={e => updateResident(rIdx, 'discount', parseFloat(e.target.value) || 0)}
                                                style={{ ...NUM_INPUT, width: '60px' }}
                                                min={0} max={100} step="0.1"
                                                disabled={!canUpdateWorksheet}
                                            />
                                        </td>
                                        {/* Net Total */}
                                        <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#16a34a', backgroundColor: '#f0fdf4', whiteSpace: 'nowrap' }}>
                                            {fmt(net)}
                                        </td>
                                        {/* Variance vs previous month */}
                                        {(() => {
                                            if (!hasPrevData) {
                                                return <td style={{ ...TD, textAlign: 'right', color: '#9ca3af', borderLeft: '1px solid #e5e7eb' }}>—</td>;
                                            }
                                            const prev = prevResidents.find(p => p.customerId === resident.customerId);
                                            if (!prev) {
                                                return <td style={{ ...TD, textAlign: 'right', fontSize: '0.75rem', color: '#9ca3af', borderLeft: '1px solid #e5e7eb' }}>New</td>;
                                            }
                                            const variance = net - residentNet(prev, displayedGLAccounts, selectedGroupIds);
                                            const isZero = Math.abs(variance) < 0.005;
                                            const color = isZero ? '#9ca3af' : variance > 0 ? '#d97706' : '#16a34a';
                                            return (
                                                <td style={{ ...TD, textAlign: 'right', fontWeight: 600, color, whiteSpace: 'nowrap', borderLeft: '1px solid #e5e7eb' }}>
                                                    {isZero ? '—' : `${variance > 0 ? '+' : ''}${fmt(variance)}`}
                                                </td>
                                            );
                                        })()}
                                        {/* Notes */}
                                        <td style={{ ...TD, padding: '2px 6px' }}>
                                            <input
                                                type="text"
                                                value={resident.notes}
                                                onChange={e => updateResident(rIdx, 'notes', e.target.value)}
                                                placeholder="Notes…"
                                                style={TEXT_INPUT}
                                                disabled={!canUpdateWorksheet}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: 700 }}>
                                <td style={{ ...FTDBLANK }} colSpan={2}>
                                    Grand Total{' '}
                                    <span style={{ fontWeight: 400, opacity: 0.6, fontSize: '0.75rem' }}>
                                        ({residents.length} residents)
                                    </span>
                                </td>
                                {displayedGroups.flatMap(bg => {
                                    const bgAccounts = groupedGL[bg.id] || [];
                                    if (bgAccounts.length === 0) return [];
                                    const unallocTotal = groupUnallocTotals[bg.id] || 0;
                                    const secGrandTotal = residents.reduce((s, r) => s + groupSubtotal(r, bgAccounts, bg.id), 0);
                                    return [
                                        ...bgAccounts.map((a, aIdx) => (
                                            <td key={a.code} style={{
                                                ...FTDBLANK, textAlign: 'right', whiteSpace: 'nowrap',
                                                borderLeft: aIdx === 0 ? '2px solid rgba(255,255,255,0.2)' : undefined,
                                            }}>
                                                {fmt(columnTotals[a.code] || 0)}
                                            </td>
                                        )),
                                        <td key={`${bg.id}-unalloc`} style={{
                                            ...FTDBLANK, textAlign: 'right', whiteSpace: 'nowrap',
                                            backgroundColor: '#273549', color: '#93c5fd',
                                        }}>
                                            {fmt(unallocTotal)}
                                        </td>,
                                        <td key={`${bg.id}-sub`} style={{
                                            ...FTDBLANK, textAlign: 'right', whiteSpace: 'nowrap',
                                            backgroundColor: '#334155',
                                        }}>
                                            {fmt(secGrandTotal)}
                                        </td>,
                                    ];
                                })}
                                <td style={{ ...FTDBLANK, textAlign: 'right', color: '#93c5fd', borderLeft: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#273549' }}>
                                    {fmt(grandNoneUnalloc)}
                                </td>
                                <td style={{ ...FTDBLANK, textAlign: 'right', color: '#e2e8f0', borderLeft: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#0f172a', whiteSpace: 'nowrap' }}>
                                    {fmt(grandTotalBefore)}
                                </td>
                                <td style={{ ...FTDBLANK, borderLeft: '2px solid rgba(255,255,255,0.2)' }} />
                                <td style={{ ...FTDBLANK, textAlign: 'right', color: '#86efac', fontSize: '0.95rem', backgroundColor: '#0f2d4f', whiteSpace: 'nowrap' }}>
                                    {fmt(grandNet)}
                                </td>
                                <td style={{ ...FTDBLANK, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', borderLeft: '1px solid rgba(255,255,255,0.2)',
                                    color: grandVariance == null || Math.abs(grandVariance) < 0.005 ? 'rgba(255,255,255,0.4)' : grandVariance > 0 ? '#fbbf24' : '#86efac',
                                }}>
                                    {grandVariance == null ? '—' : Math.abs(grandVariance) < 0.005 ? '—' : `${grandVariance > 0 ? '+' : ''}${fmt(grandVariance)}`}
                                </td>
                                <td style={FTDBLANK} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <Modal show={showUnallocModal} onHide={() => setShowUnallocModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.05rem' }}>
                        <FaExclamationTriangle style={{ color: '#c2410c', marginRight: '8px' }} />
                        Unallocated price items
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '0.875rem', color: 'var(--theme-text-secondary)', marginBottom: '14px' }}>
                        These catalogue items left remainder on this sheet. Open an item to fix GL allocations, then reload the worksheet.
                    </p>
                    {unallocatedSources.length === 0 ? (
                        <p style={{ color: 'var(--theme-text-muted)', fontSize: '0.9rem', margin: 0 }}>
                            No contributing items recorded. Reload from system to refresh this list.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {unallocatedSources.map(src => {
                                const canLink = src.key && !String(src.key).startsWith('unnamed:');
                                const reasonLabel = src.reason === 'none'
                                    ? 'No GL allocations'
                                    : src.reason === 'partial'
                                        ? 'Partial allocation'
                                        : 'Mixed';
                                return (
                                    <div
                                        key={src.key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '12px',
                                            border: '1px solid var(--theme-border)',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            backgroundColor: 'var(--theme-surface)',
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            {canLink ? (
                                                <Link
                                                    to={`/priceList?edit=${encodeURIComponent(src.key)}`}
                                                    onClick={() => setShowUnallocModal(false)}
                                                    style={{ fontWeight: 600, color: '#9a3412', textDecoration: 'underline' }}
                                                >
                                                    {src.name}
                                                    <FaExternalLinkAlt style={{ marginLeft: '6px', fontSize: '0.7rem' }} />
                                                </Link>
                                            ) : (
                                                <span style={{ fontWeight: 600 }}>{src.name}</span>
                                            )}
                                            <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)', marginTop: '2px' }}>
                                                {reasonLabel}
                                            </div>
                                        </div>
                                        <div style={{ fontWeight: 650, whiteSpace: 'nowrap' }}>
                                            R {fmt(src.amount)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUnallocModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {toast.show && <div className={`app-toast app-toast-${toast.variant}`}>{toast.message}</div>}

        </div>
    );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const TH = {
    padding: '8px 10px',
    fontWeight: 600,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    borderRight: '1px solid #e5e7eb',
};

const TD = {
    padding: '5px 10px',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'middle',
};

const FTDBLANK = {
    ...TD,
    color: 'white',
    borderTop: '2px solid rgba(255,255,255,0.2)',
    borderBottom: 'none',
};

const NUM_INPUT = {
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '3px 5px',
    width: '88px',
    textAlign: 'right',
    fontSize: '0.82rem',
    backgroundColor: '#fff',
    outline: 'none',
};

const TEXT_INPUT = {
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '3px 8px',
    width: '100%',
    fontSize: '0.8rem',
    backgroundColor: '#fff',
};

export default BillingPeriod;
